import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetRelevesDevoirsQuery,
  useGenererReleveDevoirsMutation,
  useGenererBatchDevoirsMutation,
  useUploadScanDevoirsMutation,
  useLancerOCRDevoirsMutation,
  useGetStatistiquesOCRQuery,
  useDeleteReleveDevoirsMutation,
  useRetournerReleveDevoirsMutation,
  ReleveDevoirs,
  GenererBatchClasseSpec,
} from '../../services/ocrApi';
import { useGetMatieresQuery, useGetMatiereEnseignantQuery } from '../../services/matieresApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetOptionsQuery, useGetPolesQuery, useGetClassesQuery } from '../../services/polesOptionsApi';
import {
  FileText,
  Download,
  Upload,
  Play,
  CheckCircle2,
  XCircle,
  Eye,
  Loader,
  Printer,
  AlertTriangle,
  Trash2,
  MoreVertical,
  History,
  FileSearch,
  ExternalLink,
  RefreshCw,
  Layers,
  ChevronDown,
  ChevronRight,
  Check,
  RotateCcw,
  User,
  Clock,
  Search,
  ListFilter,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import ScrollToTop from '../../components/ui/ScrollToTop';
import NiveauBanner, { getNiveauStyle, compareNiveaux } from '../../components/ui/NiveauBanner';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';
import UploadBatchParQRModal from '../../components/Releves/UploadBatchParQRModal';
import HistoriqueImportsModal from '../../components/Releves/HistoriqueImportsModal';

type NiveauType = 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
// Semestre stocké comme numéro string ('1'-'6'), converti en 'S1'/'S2' pour l'API

const statusLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  genere: { label: 'Généré', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300', icon: <FileText size={14} /> },
  imprime: { label: 'Imprimé', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300', icon: <Printer size={14} /> },
  rempli: { label: 'Rempli', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300', icon: <FileText size={14} /> },
  scanne: { label: 'Scanné', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300', icon: <Upload size={14} /> },
  traite: { label: 'Traité', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300', icon: <Play size={14} /> },
  verifie: { label: 'Vérifié', color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300', icon: <Eye size={14} /> },
  valide: { label: 'Validé', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', icon: <CheckCircle2 size={14} /> },
};

const GenerationReleveDevoirs: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State pour le formulaire de génération - Ordre: Niveau, Option, Semestre PUIS Matière
  const [selectedNiveau, setSelectedNiveau] = useState<NiveauType | ''>('');
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState<string>('');
  const [selectedMatiere, setSelectedMatiere] = useState<string>('');
  const [enseignantNom, setEnseignantNom] = useState('');
  const [enseignantPrenoms, setEnseignantPrenoms] = useState('');
  const [enseignantEditable, setEnseignantEditable] = useState(false);
  const [dateEvaluation, setDateEvaluation] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<'cases_separees' | 'zone_guidee' | 'simple'>('cases_separees');

  // State pour l'upload
  const [selectedReleveForUpload, setSelectedReleveForUpload] = useState<ReleveDevoirs | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // State pour les modals
  const [selectedReleveForDetails, setSelectedReleveForDetails] = useState<ReleveDevoirs | null>(null);
  const [selectedReleveForDelete, setSelectedReleveForDelete] = useState<ReleveDevoirs | null>(null);
  const [selectedReleveForRetour, setSelectedReleveForRetour] = useState<ReleveDevoirs | null>(null);
  const [retourSupprimerScan, setRetourSupprimerScan] = useState(false);
  const [retourMotif, setRetourMotif] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [ocrEnCours, setOcrEnCours] = useState<Set<number>>(new Set());
  const [ocrTimers, setOcrTimers] = useState<Record<number, number>>({});

  // Modal upload en lot par QR
  const [showUploadBatchQR, setShowUploadBatchQR] = useState(false);
  // Modal historique des imports
  const [showHistoriqueImports, setShowHistoriqueImports] = useState(false);

  // Mode batch
  const [modeBatch, setModeBatch] = useState(false);
  const [batchPole, setBatchPole] = useState('');
  const [batchNiveau, setBatchNiveau] = useState('');
  const [batchSemestre, setBatchSemestre] = useState('');
  const [batchFormat, setBatchFormat] = useState<'cases_separees' | 'zone_guidee' | 'simple'>('cases_separees');
  const [batchDateEvaluation, setBatchDateEvaluation] = useState('');
  const [batchSelectedClasses, setBatchSelectedClasses] = useState<Set<string>>(new Set());
  const [batchResultat, setBatchResultat] = useState<{
    total_generes: number;
    total_erreurs: number;
    resultats: { code_releve: string; matiere_code: string; matiere_nom: string; classe: string; nombre_etudiants: number; nombre_pages: number }[];
    erreurs: string[];
  } | null>(null);

  // Groupes repliés
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Niveaux repliés (séparé des classes — permet de replier tout L1 en 1 clic)
  const [collapsedNiveaux, setCollapsedNiveaux] = useState<Set<string>>(new Set());

  // ─── Filtres rapides pour la liste des relevés ─────────────────────────
  // statut tab : 'tous' | 'a_traiter' (genere/imprime/rempli/scanne) | 'traites' (traite/verifie) | 'valides' (valide)
  // Persistés dans localStorage pour rester d'une session à l'autre.
  const [filterStatut, setFilterStatut] = useState<'tous' | 'a_traiter' | 'traites' | 'valides'>(
    () => (localStorage.getItem('rd_filterStatut') as any) || 'tous'
  );
  const [filterNiveau, setFilterNiveau] = useState<string>(
    () => localStorage.getItem('rd_filterNiveau') || ''
  );
  const [filterPole, setFilterPole] = useState<string>(
    () => localStorage.getItem('rd_filterPole') || ''
  );
  const [filterSemestreParity, setFilterSemestreParity] = useState<'' | 'impair' | 'pair'>(
    () => (localStorage.getItem('rd_filterSemestreParity') as any) || ''
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    () => localStorage.getItem('rd_searchQuery') || ''
  );

  useEffect(() => { localStorage.setItem('rd_filterStatut', filterStatut); }, [filterStatut]);
  useEffect(() => { localStorage.setItem('rd_filterNiveau', filterNiveau); }, [filterNiveau]);
  useEffect(() => { localStorage.setItem('rd_filterPole', filterPole); }, [filterPole]);
  useEffect(() => { localStorage.setItem('rd_filterSemestreParity', filterSemestreParity); }, [filterSemestreParity]);
  useEffect(() => { localStorage.setItem('rd_searchQuery', searchQuery); }, [searchQuery]);

  // Helper parité semestre
  const semestreIsParity = (semestre: string | number | undefined | null, parity: 'impair' | 'pair'): boolean => {
    if (!semestre) return false;
    const n = parseInt(String(semestre).replace(/\D/g, ''), 10);
    if (isNaN(n)) return false;
    return parity === 'impair' ? n % 2 === 1 : n % 2 === 0;
  };

  // Messages
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Queries et Mutations
  const { data: matieres } = useGetMatieresQuery();
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: classes } = useGetClassesQuery();
  // Passe le param semestre (S1/S2) aux stats + à la liste si filtre actif,
  // pour que les chiffres reflètent exactement la session en cours d'analyse.
  const semestreApi = filterSemestreParity === 'pair' ? 'S2'
    : filterSemestreParity === 'impair' ? 'S1'
    : undefined;
  const { data: releves, isLoading: relevesLoading, refetch: refetchReleves } = useGetRelevesDevoirsQuery(
    semestreApi ? { semestre: semestreApi } : undefined,
  );
  const { data: statistiques } = useGetStatistiquesOCRQuery(
    semestreApi ? { semestre: semestreApi } : undefined,
  );
  const { data: anneeActive } = useGetAnneeActiveQuery();

  // Récupérer l'enseignant de la matière sélectionnée
  const matiereIdParsed = selectedMatiere ? parseInt(selectedMatiere) : 0;
  const { data: enseignantData } = useGetMatiereEnseignantQuery(
    { matiereId: matiereIdParsed, anneeAcademique: anneeActive?.libelle },
    { skip: !selectedMatiere || !matiereIdParsed || isNaN(matiereIdParsed) || !anneeActive }
  );

  const [genererReleve, { isLoading: genererLoading }] = useGenererReleveDevoirsMutation();
  const [genererBatch, { isLoading: batchLoading }] = useGenererBatchDevoirsMutation();
  const [uploadScan, { isLoading: uploadLoading }] = useUploadScanDevoirsMutation();
  const [lancerOCR, { isLoading: ocrLoading }] = useLancerOCRDevoirsMutation();
  const [deleteReleve, { isLoading: deleteLoading }] = useDeleteReleveDevoirsMutation();
  const [retournerReleve, { isLoading: retourLoading }] = useRetournerReleveDevoirsMutation();

  // Obtenir les semestres disponibles selon le niveau
  const getSemestresForNiveau = (niveau: string): { value: string; label: string }[] => {
    switch (niveau) {
      case 'L1':
        return [
          { value: '1', label: 'Semestre 1 (S1)' },
          { value: '2', label: 'Semestre 2 (S2)' },
        ];
      case 'L2':
        return [
          { value: '3', label: 'Semestre 3 (S3)' },
          { value: '4', label: 'Semestre 4 (S4)' },
        ];
      case 'L3':
        return [
          { value: '5', label: 'Semestre 5 (S5)' },
          { value: '6', label: 'Semestre 6 (S6)' },
        ];
      // Les matières des Masters sont stockées en semestre ABSOLU 7-10
      // (M1 → S7/S8, M2 → S9/S10). Les valeurs doivent donc être 7-10 pour
      // retrouver les matières ; getSemestreForApi les remappe en S1/S2 (parité).
      case 'M1':
        return [
          { value: '7', label: 'Semestre 1 (S1)' },
          { value: '8', label: 'Semestre 2 (S2)' },
        ];
      case 'M2':
        return [
          { value: '9', label: 'Semestre 1 (S1)' },
          { value: '10', label: 'Semestre 2 (S2)' },
        ];
      default:
        return [];
    }
  };

  const semestresDisponibles = useMemo(() => getSemestresForNiveau(selectedNiveau), [selectedNiveau]);

  // Filtrer les matières selon niveau, option et semestre
  const filteredMatieres = useMemo(() => {
    if (!matieres || !selectedNiveau || !selectedOption || !selectedSemestre) {
      return [];
    }
    const semestreNum = parseInt(selectedSemestre);
    return matieres.filter(m => {
      const matchNiveau = m.niveau === selectedNiveau;
      const matchOption = m.option?.toLowerCase() === selectedOption.toLowerCase();
      const matchSemestre = m.semestre === semestreNum;
      return matchNiveau && matchOption && matchSemestre;
    });
  }, [matieres, selectedNiveau, selectedOption, selectedSemestre]);

  // Mettre à jour l'enseignant quand une matière est sélectionnée
  useEffect(() => {
    if (enseignantData) {
      if (enseignantData.enseignant_nom) {
        setEnseignantNom(enseignantData.enseignant_nom_seul || enseignantData.enseignant_nom);
        setEnseignantPrenoms(enseignantData.enseignant_prenoms || '');
        setEnseignantEditable(false);
      } else {
        setEnseignantNom('');
        setEnseignantPrenoms('');
        setEnseignantEditable(true);
      }
    }
  }, [enseignantData]);

  // Réinitialiser le semestre quand le niveau change
  useEffect(() => {
    setSelectedSemestre('');
  }, [selectedNiveau]);

  // Réinitialiser la matière quand niveau/option/semestre change
  useEffect(() => {
    setSelectedMatiere('');
    setEnseignantNom('');
    setEnseignantPrenoms('');
    setEnseignantEditable(false);
  }, [selectedNiveau, selectedOption, selectedSemestre]);

  // Convertir le semestre numérique en format S1/S2 pour l'API
  const getSemestreForApi = (semestre: string): 'S1' | 'S2' => {
    const num = parseInt(semestre);
    // S1, S3, S5 → S1 (semestres impairs)
    // S2, S4, S6 → S2 (semestres pairs)
    return num % 2 === 1 ? 'S1' : 'S2';
  };

  // URL de base — vide en production (utilise le même domaine), localhost en dev
  const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';

  // Compteurs par statut (avant filtrage) pour les onglets
  const compteursStatut = useMemo(() => {
    if (!releves || !Array.isArray(releves)) return { tous: 0, a_traiter: 0, traites: 0, valides: 0 };
    const c = { tous: releves.length, a_traiter: 0, traites: 0, valides: 0 };
    releves.forEach(r => {
      if (['valide'].includes(r.status)) c.valides++;
      else if (['traite', 'verifie'].includes(r.status)) c.traites++;
      else c.a_traiter++;
    });
    return c;
  }, [releves]);

  // Grouper les relevés par classe (niveau + option) — APRÈS application des filtres
  const groupedReleves = useMemo(() => {
    if (!releves || !Array.isArray(releves) || releves.length === 0) return [];

    // 1) Filtrage par statut (onglets)
    let filtered = releves;
    if (filterStatut === 'valides') {
      filtered = filtered.filter(r => r.status === 'valide');
    } else if (filterStatut === 'traites') {
      filtered = filtered.filter(r => ['traite', 'verifie'].includes(r.status));
    } else if (filterStatut === 'a_traiter') {
      filtered = filtered.filter(r => !['traite', 'verifie', 'valide'].includes(r.status));
    }

    // 2) Filtrage par niveau
    if (filterNiveau) {
      filtered = filtered.filter(r => r.niveau === filterNiveau);
    }

    // 2bis) Filtrage par pôle (via mapping option → pole_nom)
    if (filterPole) {
      const optionToPole = new Map<string, string>();
      (options || []).forEach(o => optionToPole.set(o.nom, o.pole_nom));
      filtered = filtered.filter(r => {
        const pn = optionToPole.get(r.option || '');
        return pn === filterPole;
      });
    }

    // 2ter) Filtrage par parité du semestre (impair / pair)
    if (filterSemestreParity) {
      filtered = filtered.filter(r => semestreIsParity(r.semestre, filterSemestreParity));
    }

    // 3) Recherche texte (code, matière, option, enseignant)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(r =>
        (r.code_releve || '').toLowerCase().includes(q) ||
        (r.matiere_nom || '').toLowerCase().includes(q) ||
        (r.matiere_code || '').toLowerCase().includes(q) ||
        (r.option || '').toLowerCase().includes(q) ||
        (r.enseignant_nom || '').toLowerCase().includes(q)
      );
    }

    // 4) Groupement
    const groups: Record<string, { key: string; label: string; niveau: string; releves: ReleveDevoirs[] }> = {};
    const optionsMap = new Map<string, string>();
    (options || []).forEach(o => optionsMap.set(o.nom, o.code));

    filtered.forEach((r) => {
      const niv = r.niveau || '?';
      const opt = r.option || 'Sans option';
      const optCode = optionsMap.get(opt) || opt;
      const key = `${niv}_${opt}`;
      if (!groups[key]) {
        const classeLabel = opt !== 'Sans option' ? `${niv}${optCode}` : niv;
        const fullLabel = opt !== 'Sans option' ? `${classeLabel} (${opt})` : `${niv} — Sans option`;
        groups[key] = { key, label: fullLabel, niveau: niv, releves: [] };
      }
      groups[key].releves.push(r);
    });

    // Trier : par niveau puis par option
    return Object.values(groups).sort((a, b) => {
      if (a.niveau !== b.niveau) return a.niveau.localeCompare(b.niveau);
      return a.label.localeCompare(b.label);
    });
  }, [releves, options, filterStatut, filterNiveau, filterPole, filterSemestreParity, searchQuery]);

  // Re-groupement à 2 niveaux : d'abord par Niveau (L1, L2…) puis par Classe
  // pour offrir une distinction visuelle nette entre niveaux.
  const niveauxGroupes = useMemo(() => {
    const byNiveau: Record<string, { niveau: string; classes: typeof groupedReleves; nbReleves: number }> = {};
    for (const g of groupedReleves) {
      const niv = g.niveau || '?';
      if (!byNiveau[niv]) byNiveau[niv] = { niveau: niv, classes: [], nbReleves: 0 };
      byNiveau[niv].classes.push(g);
      byNiveau[niv].nbReleves += g.releves.length;
    }
    return Object.values(byNiveau).sort((a, b) => compareNiveaux(a.niveau, b.niveau));
  }, [groupedReleves]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  }, []);

  const toggleNiveau = useCallback((niveau: string) => {
    setCollapsedNiveaux(prev => {
      const s = new Set(prev);
      s.has(niveau) ? s.delete(niveau) : s.add(niveau);
      return s;
    });
  }, []);

  // Helper local : utilise le helper partagé
  const niveauStyle = getNiveauStyle;

  // Générer un nouveau relevé
  const handleGenererReleve = async () => {
    if (!selectedMatiere || !selectedNiveau || !selectedOption || !selectedSemestre) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }

    if (!anneeActive) {
      setMessage({ type: 'error', text: 'Aucune année académique active' });
      return;
    }

    try {
      const result = await genererReleve({
        matiere_id: parseInt(selectedMatiere),
        niveau: selectedNiveau,
        option: selectedOption,
        semestre: getSemestreForApi(selectedSemestre),
        annee_academique: anneeActive.libelle,
        enseignant_nom: enseignantNom || undefined,
        enseignant_prenoms: enseignantPrenoms || undefined,
        date_evaluation: dateEvaluation || undefined,
        format: selectedFormat,
      }).unwrap();

      setMessage({
        type: 'success',
        text: `Relevé ${result.code_releve} généré avec succès (${result.nombre_etudiants} étudiants, ${result.nombre_pages} pages)`,
      });

      // Réinitialiser le formulaire
      setSelectedMatiere('');
      setSelectedNiveau('');
      setSelectedOption('');
      setSelectedSemestre('');
      setEnseignantNom('');
      setEnseignantPrenoms('');
      setEnseignantEditable(false);
      setDateEvaluation('');

      refetchReleves();
    } catch (error: any) {
      if (error?.status === 409 && error?.data?.releve_existant) {
        const existant = error.data.releve_existant;
        setMessage({
          type: 'error',
          text: `Un relevé identique existe déjà (${existant.code_releve} — ${existant.status_label}). Supprimez-le avant d'en générer un nouveau.`,
        });
      } else {
        setMessage({
          type: 'error',
          text: error?.data?.error || 'Erreur lors de la génération du relevé',
        });
      }
    }
  };

  // Upload du scan
  const handleUploadScan = async () => {
    if (!selectedReleveForUpload || !uploadFile) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un relevé et un fichier' });
      return;
    }

    // Capturer l'id AVANT le await pour éviter qu'un rerender ne le perde
    const releveId = selectedReleveForUpload.id;

    // Fermer la modal IMMÉDIATEMENT — l'upload se déroule en arrière-plan.
    // Si erreur, on rouvre rien (le user verra le toast + le statut du relevé inchangé).
    setSelectedReleveForUpload(null);
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setMessage({ type: 'success', text: 'Upload en cours…' });
    // Marquer immédiatement l'OCR comme en cours côté UI (le backend va auto-lancer)
    setOcrEnCours(prev => { const next = new Set(prev); next.add(releveId); return next; });
    setOcrTimers(prev => ({ ...prev, [releveId]: Date.now() }));

    try {
      const formData = new FormData();
      formData.append('fichier', uploadFile);

      const result = await uploadScan({
        releveId,
        formData,
      }).unwrap();

      if (result.ocr_status === 'lance') {
        setMessage({
          type: 'success',
          text: result.is_reupload
            ? 'Fichier re-uploadé — traitement OCR relancé automatiquement'
            : 'Fichier uploadé — traitement OCR lancé automatiquement',
        });
      } else {
        // Pas d'auto-OCR : retirer du tracking
        setOcrEnCours(prev => { const next = new Set(prev); next.delete(releveId); return next; });
        setMessage({ type: 'success', text: result.message || 'Fichier uploadé avec succès' });
      }
      refetchReleves();
    } catch (error: any) {
      // L'upload a probablement réussi côté serveur malgré l'erreur HTTP (timeout réseau, etc.).
      // On force un refetch pour vérifier l'état réel et on prévient l'utilisateur.
      refetchReleves();
      setMessage({
        type: 'error',
        text: (error?.data?.error || error?.error || 'Erreur réseau') +
              ' — vérifiez le statut du relevé. Si l\'upload a réussi malgré ce message, l\'OCR continue en arrière-plan.',
      });
      // Retirer du tracking après un délai si pas de progression
      setTimeout(() => {
        setOcrEnCours(prev => {
          const next = new Set(prev);
          next.delete(releveId);
          return next;
        });
      }, 3000);
    }
  };

  // Lancer le traitement OCR
  const handleLancerOCR = async (releve: ReleveDevoirs) => {
    try {
      const result = await lancerOCR(releve.id).unwrap();
      // Ajouter au tracking OCR en cours + démarrer le timer
      setOcrEnCours(prev => { const next = new Set(prev); next.add(releve.id); return next; });
      setOcrTimers(prev => ({ ...prev, [releve.id]: Date.now() }));
      setMessage({
        type: 'success',
        text: `Traitement OCR lancé pour ${releve.code_releve}...`,
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors du lancement du traitement OCR',
      });
    }
  };

  // Timer qui force le re-render chaque seconde pendant l'OCR
  const [, setTick] = useState(0);
  useEffect(() => {
    if (ocrEnCours.size === 0) return;
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickInterval);
  }, [ocrEnCours.size]);

  const formatTimer = (startTime: number) => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  };

  // Polling pour suivre l'avancement de l'OCR
  useEffect(() => {
    if (ocrEnCours.size === 0) return;

    const interval = setInterval(() => {
      refetchReleves().then(({ data }) => {
        if (!data || !Array.isArray(data)) return;
        const terminés: number[] = [];
        Array.from(ocrEnCours).forEach(id => {
          const rel = data.find((r: ReleveDevoirs) => r.id === id);
          if (rel && rel.status !== 'scanne') {
            terminés.push(id);
          }
        });
        if (terminés.length > 0) {
          setOcrEnCours(prev => {
            const next = new Set(prev);
            terminés.forEach(id => next.delete(id));
            return next;
          });
          // Nettoyer les timers et afficher la durée
          const durees = terminés.map(id => ocrTimers[id] ? formatTimer(ocrTimers[id]) : '').filter(Boolean);
          setOcrTimers(prev => {
            const next = { ...prev };
            terminés.forEach(id => delete next[id]);
            return next;
          });
          setMessage({
            type: 'success',
            text: `OCR terminé pour ${terminés.length} relevé(s)${durees.length ? ` en ${durees.join(', ')}` : ''} !`,
          });
        }
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [ocrEnCours, refetchReleves]);

  // Headers communs pour les appels fetch directs
  const getFetchHeaders = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const annee = localStorage.getItem('annee_academique') || anneeActive?.libelle;
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    return headers;
  };

  // Télécharger le PDF
  const handleDownloadPDF = async (releve: ReleveDevoirs) => {
    try {
      const response = await fetch(`${baseUrl}/api/ocr/releves-devoirs/${releve.id}/telecharger_pdf/`, {
        headers: getFetchHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${releve.code_releve}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setMessage({
          type: 'error',
          text: 'Erreur lors du téléchargement du PDF',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Erreur lors du téléchargement du PDF',
      });
    }
  };

  // Aperçu du PDF (dans un nouvel onglet)
  const handlePreviewPDF = async (releve: ReleveDevoirs) => {
    try {
      const response = await fetch(`${baseUrl}/api/ocr/releves-devoirs/${releve.id}/telecharger_pdf/`, {
        headers: getFetchHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setMessage({
          type: 'error',
          text: 'Erreur lors de l\'aperçu du PDF',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Erreur lors de l\'aperçu du PDF',
      });
    }
  };

  // Retourner un relevé en cours (reset statut → 'scanne' ou 'genere')
  const handleRetournerEnCours = async () => {
    if (!selectedReleveForRetour) return;
    const payload = {
      releveId: selectedReleveForRetour.id,
      supprimer_scan: retourSupprimerScan,
      motif: retourMotif.trim(),
    };
    try {
      const res = await retournerReleve(payload).unwrap();
      // Fermer la modal IMMÉDIATEMENT après succès
      setSelectedReleveForRetour(null);
      setRetourSupprimerScan(false);
      setRetourMotif('');
      setMessage({ type: 'success', text: res.message });
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors du retour en cours',
      });
    }
  };

  // Supprimer un relevé
  const handleDeleteReleve = async () => {
    if (!selectedReleveForDelete) return;

    try {
      await deleteReleve(selectedReleveForDelete.id).unwrap();
      setMessage({ type: 'success', text: `Relevé ${selectedReleveForDelete.code_releve} supprimé avec succès` });
      setSelectedReleveForDelete(null);
      selectedIds.delete(selectedReleveForDelete.id);
      setSelectedIds(new Set(selectedIds));
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || error?.data?.detail || 'Erreur lors de la suppression du relevé',
      });
    }
  };

  // Sélection / Désélection
  const toggleSelect = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectClasse = (classeReleves: ReleveDevoirs[]) => {
    const ids = classeReleves.map(r => r.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) {
      ids.forEach(id => next.delete(id));
    } else {
      ids.forEach(id => next.add(id));
    }
    setSelectedIds(next);
  };

  // ─── Impression / Téléchargement groupé ─────────────────────────────────

  // État de progression pour le téléchargement groupé
  const [bulkPrintLoading, setBulkPrintLoading] = useState<{ active: boolean; current: number; total: number }>({ active: false, current: 0, total: 0 });

  /** Télécharge tous les PDFs sélectionnés un par un (séquentiel pour éviter de saturer le navigateur). */
  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Télécharger ${selectedIds.size} PDF(s) ? Ils arriveront un par un dans votre dossier de téléchargements.`)) return;

    const ids = Array.from(selectedIds);
    const releveById = new Map((releves || []).map((r: any) => [r.id, r]));
    setBulkPrintLoading({ active: true, current: 0, total: ids.length });

    let success = 0;
    let errors = 0;
    for (let i = 0; i < ids.length; i++) {
      const releve: any = releveById.get(ids[i]);
      if (!releve) { errors++; continue; }
      try {
        const response = await fetch(
          `${baseUrl}/api/ocr/releves-devoirs/${releve.id}/telecharger_pdf/`,
          { headers: getFetchHeaders() },
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${releve.code_releve}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Pause courte pour laisser le navigateur traiter chaque téléchargement
          await new Promise((r) => setTimeout(r, 250));
          window.URL.revokeObjectURL(url);
          success++;
        } else {
          errors++;
        }
      } catch {
        errors++;
      }
      setBulkPrintLoading({ active: true, current: i + 1, total: ids.length });
    }
    setBulkPrintLoading({ active: false, current: 0, total: 0 });
    setMessage({
      type: errors > 0 ? 'error' : 'success',
      text: `${success}/${ids.length} PDF(s) téléchargé(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`,
    });
  };

  /**
   * Crée une page d'impression unique contenant tous les PDFs sélectionnés
   * empilés en iframes — l'utilisateur lance Ctrl+P pour tout imprimer d'un coup.
   */
  const handleBulkPrint = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Préparer l'impression groupée de ${selectedIds.size} relevé(s) ?\n\nUne fenêtre va s'ouvrir avec tous les PDFs empilés. Vous pourrez lancer Ctrl+P pour tout imprimer d'un coup.`)) return;

    const ids = Array.from(selectedIds);
    const releveById = new Map((releves || []).map((r: any) => [r.id, r]));
    setBulkPrintLoading({ active: true, current: 0, total: ids.length });

    // 1) Récupérer tous les PDFs en blob
    const items: { code: string; url: string }[] = [];
    for (let i = 0; i < ids.length; i++) {
      const releve: any = releveById.get(ids[i]);
      if (!releve) continue;
      try {
        const response = await fetch(
          `${baseUrl}/api/ocr/releves-devoirs/${releve.id}/telecharger_pdf/`,
          { headers: getFetchHeaders() },
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          items.push({ code: releve.code_releve, url });
        }
      } catch {
        /* skip */
      }
      setBulkPrintLoading({ active: true, current: i + 1, total: ids.length });
    }
    setBulkPrintLoading({ active: false, current: 0, total: 0 });

    if (items.length === 0) {
      setMessage({ type: 'error', text: 'Aucun PDF n\'a pu être chargé' });
      return;
    }

    // 2) Construire la page d'impression empilée
    const win = window.open('', '_blank');
    if (!win) {
      setMessage({ type: 'error', text: 'Le popup a été bloqué — autorise les popups pour ce site' });
      return;
    }
    const html = `
      <!DOCTYPE html>
      <html><head><meta charset="utf-8"><title>Impression groupée (${items.length} relevés)</title>
      <style>
        body { margin: 0; padding: 0; background: #525659; }
        .topbar { position: sticky; top: 0; background: #1f2937; color: #fff; padding: 12px 20px; z-index: 100; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,.3); }
        .topbar h1 { margin: 0; font: 600 16px sans-serif; }
        .topbar button { background: #2563eb; color: #fff; border: 0; padding: 8px 16px; border-radius: 6px; font: 600 14px sans-serif; cursor: pointer; }
        .topbar button:hover { background: #1d4ed8; }
        .pdf-item { margin: 0; }
        .pdf-item .label { background: #fff; padding: 8px 16px; font: 600 14px sans-serif; color: #111; border-bottom: 1px solid #e5e7eb; }
        .pdf-item iframe { width: 100%; height: 100vh; border: 0; display: block; background: #fff; }
        @media print {
          .topbar, .pdf-item .label { display: none !important; }
          body { background: #fff; }
          .pdf-item { page-break-after: always; }
          .pdf-item iframe { height: 100vh; }
        }
      </style></head><body>
        <div class="topbar">
          <h1>${items.length} relevé(s) prêt(s) à imprimer</h1>
          <button onclick="window.print()">🖨️ Imprimer tout (Ctrl+P)</button>
        </div>
        ${items.map(it => `
          <div class="pdf-item">
            <div class="label">${it.code}</div>
            <iframe src="${it.url}#toolbar=0&view=FitH"></iframe>
          </div>
        `).join('')}
      </body></html>
    `;
    win.document.open();
    win.document.write(html);
    win.document.close();
    setMessage({
      type: 'success',
      text: `${items.length} relevé(s) chargé(s). Lance Ctrl+P dans la nouvelle fenêtre pour tout imprimer.`,
    });
  };

  // Suppression en masse
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.size} relevé(s) ? Cette action est irréversible.`)) return;

    setBulkDeleteLoading(true);
    let success = 0;
    let errors = 0;
    const ids = Array.from(selectedIds);
    for (let i = 0; i < ids.length; i++) {
      try {
        await deleteReleve(ids[i]).unwrap();
        success++;
      } catch {
        errors++;
      }
    }
    setBulkDeleteLoading(false);
    setSelectedIds(new Set());
    refetchReleves();
    setMessage({
      type: errors > 0 ? 'error' : 'success',
      text: `${success} relevé(s) supprimé(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`,
    });
  };

  // ─── Batch logic ──────────────────────────────────────────────────────────

  // Obtenir le semestre API (S1/S2) à partir du niveau et du semestre affiché
  const getBatchSemestresForNiveau = (niveau: string): { value: string; label: string; apiValue: string }[] => {
    switch (niveau) {
      case 'L1': return [
        { value: '1', label: 'Semestre 1 (S1)', apiValue: 'S1' },
        { value: '2', label: 'Semestre 2 (S2)', apiValue: 'S2' },
      ];
      case 'L2': return [
        { value: '3', label: 'Semestre 3 (S3)', apiValue: 'S1' },
        { value: '4', label: 'Semestre 4 (S4)', apiValue: 'S2' },
      ];
      case 'L3': return [
        { value: '5', label: 'Semestre 5 (S5)', apiValue: 'S1' },
        { value: '6', label: 'Semestre 6 (S6)', apiValue: 'S2' },
      ];
      default: return [
        { value: '1', label: 'Semestre 1 (S1)', apiValue: 'S1' },
        { value: '2', label: 'Semestre 2 (S2)', apiValue: 'S2' },
      ];
    }
  };

  // Classes filtrées pour le batch
  const batchFilteredClasses = useMemo(() => {
    if (!classes || !Array.isArray(classes)) return [];
    let result = classes.filter((c) => c.actif);
    if (batchPole) result = result.filter((c) => c.pole_nom === batchPole);
    if (batchNiveau) result = result.filter((c) => c.niveau === batchNiveau);
    return result.sort((a, b) => {
      const niveaux = ['L1', 'L2', 'L3', 'M1', 'M2'];
      const ni = niveaux.indexOf(a.niveau) - niveaux.indexOf(b.niveau);
      if (ni !== 0) return ni;
      return a.option_nom.localeCompare(b.option_nom);
    });
  }, [classes, batchPole, batchNiveau]);

  // Toggle une classe dans la sélection batch
  const toggleBatchClass = (key: string) => {
    setBatchSelectedClasses((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Sélectionner / Désélectionner tout
  const toggleAllBatchClasses = () => {
    if (batchSelectedClasses.size === batchFilteredClasses.length) {
      setBatchSelectedClasses(new Set());
    } else {
      setBatchSelectedClasses(new Set(batchFilteredClasses.map((c) => `${c.niveau}|${c.option_nom}`)));
    }
  };

  // Lancer la génération batch
  const handleGenererBatch = async () => {
    if (batchSelectedClasses.size === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner au moins une classe' });
      return;
    }
    if (!batchSemestre) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un semestre' });
      return;
    }
    if (!anneeActive) {
      setMessage({ type: 'error', text: 'Aucune année académique active' });
      return;
    }

    // Déterminer le semestre API
    const niveauRef = batchNiveau || 'L1';
    const semestreInfo = getBatchSemestresForNiveau(niveauRef).find((s) => s.value === batchSemestre);
    const semestreApi = semestreInfo?.apiValue || 'S1';

    const classesPayload: GenererBatchClasseSpec[] = [];
    batchSelectedClasses.forEach((key) => {
      const [niveau, option] = key.split('|');
      classesPayload.push({ niveau, option, semestre: semestreApi });
    });

    try {
      setBatchResultat(null);
      const result = await genererBatch({
        classes: classesPayload,
        annee_academique: anneeActive.libelle,
        format: batchFormat,
        date_evaluation: batchDateEvaluation || undefined,
      }).unwrap();

      setBatchResultat(result);

      if (result.total_generes > 0) {
        setMessage({
          type: 'success',
          text: `${result.total_generes} relevé(s) généré(s) avec succès${result.total_erreurs > 0 ? ` (${result.total_erreurs} erreur(s))` : ''}`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `Aucun relevé généré. ${result.erreurs.length > 0 ? result.erreurs[0] : ''}`,
        });
      }

      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la génération en batch',
      });
    }
  };

  // Reset batch semestre when niveau changes
  useEffect(() => {
    setBatchSemestre('');
  }, [batchNiveau]);

  // Aller à la vérification
  const handleVerifier = (releve: ReleveDevoirs) => {
    navigate(`/ocr/verification-devoirs/${releve.id}`);
  };

  // Formater la date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 py-3 dark:bg-gray-900 min-h-screen">
      {/* Header compact (refonte 2026-09-01 : text-3xl+desc énorme → PageHeader
          + barre stats inline. Gain vertical ≈ 200 px pour laisser + de place
          à la liste des relevés). */}
      <div className="mb-3">
        <PageHeader
          title="Relevés de Devoirs — OCR"
          description={
            <span className="inline-flex items-center gap-1.5">
              {statistiques && (
                <span className="tabular-nums font-medium text-gray-700 dark:text-gray-300">
                  {statistiques.total_releves_devoirs}
                </span>
              )}
              <span>relevé(s) · générés, uploadés, traités par OCR</span>
            </span>
          }
          actions={
            <button
              onClick={() => refetchReleves()}
              className="inline-flex items-center px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 gap-1.5"
              title="Actualiser la liste"
            >
              <RefreshCw size={14} /> Actualiser
            </button>
          }
        />
      </div>

      {/* Message de feedback (unifié) */}
      {message && (
        <div className="mb-3">
          <AlertBanner
            type={message.type === 'success' ? 'success' : 'error'}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}

      {/* Statistiques — barre chips compacte (au lieu des 5 grosses cards).
          Cliquables : chaque chip filtre la liste sur son statut. */}
      {statistiques && (() => {
        const chips = [
          { key: 'tous',      label: 'Total',      value: statistiques.total_releves_devoirs, icon: <FileText size={12} />,     tone: 'gray',    filterStatut: 'tous' as const },
          { key: 'attente',   label: 'En attente', value: statistiques.releves_en_attente,   icon: <Upload size={12} />,       tone: 'orange',  filterStatut: 'a_traiter' as const },
          { key: 'verifier',  label: 'À vérifier', value: statistiques.releves_a_verifier,   icon: <Eye size={12} />,          tone: 'yellow',  filterStatut: 'traites' as const },
          { key: 'valides',   label: 'Validés',    value: statistiques.releves_valides,      icon: <CheckCircle2 size={12} />, tone: 'emerald', filterStatut: 'valides' as const },
          { key: 'confiance', label: 'Confiance',  value: `${Number(statistiques.confiance_moyenne).toFixed(1)}%`, icon: <Play size={12} />, tone: 'blue', filterStatut: null },
        ];
        const toneClasses: Record<string, string> = {
          gray:    'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
          orange:  'text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
          yellow:  'text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700',
          emerald: 'text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
          blue:    'text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
        };
        const toneActiveBg: Record<string, string> = {
          gray:    'bg-gray-600 text-white border-gray-600',
          orange:  'bg-orange-600 text-white border-orange-600',
          yellow:  'bg-yellow-500 text-white border-yellow-500',
          emerald: 'bg-emerald-600 text-white border-emerald-600',
          blue:    'bg-blue-600 text-white border-blue-600',
        };
        return (
          <div className="mb-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
            {chips.map(c => {
              const active = c.filterStatut !== null && filterStatut === c.filterStatut;
              const clickable = c.filterStatut !== null;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => clickable && setFilterStatut(c.filterStatut!)}
                  disabled={!clickable}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                    active ? toneActiveBg[c.tone] : toneClasses[c.tone]
                  } ${clickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                  title={clickable ? `Filtrer sur "${c.label}"` : c.label}
                >
                  {c.icon}
                  <span className="font-medium">{c.label}</span>
                  <span className="font-bold tabular-nums">{c.value}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Refonte 2026-09-01 : 3→4 colonnes. Génération = 1/4 (25%),
          Liste = 3/4 (75%). Ancien 33/66 laissait trop de vide dans la col
          gauche et écrasait la liste à droite. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Formulaire de génération (compact) */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            {/* Onglets: Unitaire / Batch */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
              <button
                onClick={() => setModeBatch(false)}
                className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  !modeBatch
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <FileText size={16} className="mr-1.5" />
                Unitaire
              </button>
              <button
                onClick={() => setModeBatch(true)}
                className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  modeBatch
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Layers size={16} className="mr-1.5" />
                Batch
              </button>
            </div>

            {modeBatch ? (
              /* ═══════ BATCH MODE ═══════ */
              <div className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Générez des relevés pour toutes les matières de plusieurs classes en une seule opération.
                </p>

                {/* Filtres */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Pôle */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Pôle</label>
                    <select
                      value={batchPole}
                      onChange={(e) => setBatchPole(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Tous les pôles</option>
                      {Array.isArray(poles) && poles.map((p) => (
                        <option key={p.id} value={p.nom}>{p.nom}</option>
                      ))}
                    </select>
                  </div>

                  {/* Niveau */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                    <select
                      value={batchNiveau}
                      onChange={(e) => setBatchNiveau(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Tous les niveaux</option>
                      {['L1', 'L2', 'L3', 'M1', 'M2'].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Semestre */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Semestre <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={batchSemestre}
                    onChange={(e) => setBatchSemestre(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">-- Sélectionner --</option>
                    {(batchNiveau
                      ? getBatchSemestresForNiveau(batchNiveau)
                      : [
                          { value: '1', label: 'S1 (impair)', apiValue: 'S1' },
                          { value: '2', label: 'S2 (pair)', apiValue: 'S2' },
                        ]
                    ).map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Sélection des classes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Classes ({batchSelectedClasses.size}/{batchFilteredClasses.length})
                    </label>
                    <button
                      type="button"
                      onClick={toggleAllBatchClasses}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {batchSelectedClasses.size === batchFilteredClasses.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-100 dark:divide-gray-700">
                    {batchFilteredClasses.length === 0 ? (
                      <p className="p-3 text-sm text-gray-400 text-center">Aucune classe trouvée</p>
                    ) : (
                      batchFilteredClasses.map((c) => {
                        const key = `${c.niveau}|${c.option_nom}`;
                        const isSelected = batchSelectedClasses.has(key);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                              isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleBatchClass(key)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {c.code || `${c.niveau}`}
                              </span>
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                {c.option_nom}
                              </span>
                            </div>
                            {c.pole_nom && (
                              <span className="ml-1 text-xs text-gray-400 dark:text-gray-500 truncate max-w-[80px]">
                                {c.pole_nom}
                              </span>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Date d'évaluation (optionnel) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date d'évaluation <span className="text-xs text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="date"
                    value={batchDateEvaluation}
                    onChange={(e) => setBatchDateEvaluation(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                {/* Format */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Format</label>
                  <select
                    value={batchFormat}
                    onChange={(e) => setBatchFormat(e.target.value as any)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="cases_separees">Cases séparées (recommandé)</option>
                    <option value="zone_guidee">Zone guidée</option>
                    <option value="simple">Simple</option>
                  </select>
                </div>

                {/* Année académique */}
                {anneeActive && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-2">
                    <p className="text-xs text-blue-800 dark:text-blue-300">
                      <span className="font-medium">Année :</span> {anneeActive.libelle}
                    </p>
                  </div>
                )}

                {/* Bouton Générer Batch */}
                <button
                  onClick={handleGenererBatch}
                  disabled={batchLoading || batchSelectedClasses.size === 0 || !batchSemestre}
                  className="w-full flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {batchLoading ? (
                    <Loader className="animate-spin mr-2" size={20} />
                  ) : (
                    <Layers className="mr-2" size={20} />
                  )}
                  Générer {batchSelectedClasses.size} classe{batchSelectedClasses.size > 1 ? 's' : ''}
                </button>

                {/* Résultat du batch */}
                {batchResultat && (
                  <div className="mt-4 space-y-3">
                    {/* Résumé */}
                    <div className={`p-3 rounded-lg text-sm ${
                      batchResultat.total_generes > 0
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    }`}>
                      <div className="flex items-center mb-1">
                        {batchResultat.total_generes > 0 ? (
                          <CheckCircle2 size={16} className="text-green-600 mr-2" />
                        ) : (
                          <XCircle size={16} className="text-red-600 mr-2" />
                        )}
                        <span className="font-medium dark:text-gray-100">
                          {batchResultat.total_generes} relevé(s) généré(s)
                        </span>
                      </div>
                      {batchResultat.total_generes > 0 && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                          {batchResultat.resultats.reduce((s, r) => s + r.nombre_pages, 0)} pages au total
                        </p>
                      )}
                    </div>

                    {/* Détails des relevés générés */}
                    {batchResultat.resultats.length > 0 && (
                      <details className="group">
                        <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
                          <ChevronDown size={14} className="mr-1 group-open:rotate-180 transition-transform" />
                          Relevés générés ({batchResultat.resultats.length})
                        </summary>
                        <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                          {batchResultat.resultats.map((r) => (
                            <div key={r.code_releve} className="flex items-center py-1 px-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <Check size={12} className="text-green-500 mr-1.5 flex-shrink-0" />
                              <span className="font-mono text-gray-600 dark:text-gray-400 mr-2">{r.classe}</span>
                              <span className="text-gray-800 dark:text-gray-200 truncate">{r.matiere_code}</span>
                              <span className="ml-auto text-gray-400">{r.nombre_etudiants} ét.</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Erreurs */}
                    {batchResultat.erreurs.length > 0 && (
                      <details className="group">
                        <summary className="cursor-pointer text-xs font-medium text-orange-600 dark:text-orange-400 flex items-center">
                          <AlertTriangle size={14} className="mr-1" />
                          {batchResultat.erreurs.length} avertissement(s)
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto text-xs space-y-1">
                          {batchResultat.erreurs.map((e, i) => (
                            <div key={i} className="py-1 px-2 bg-orange-50 dark:bg-orange-900/10 rounded text-orange-700 dark:text-orange-300">
                              {e}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* ═══════ SINGLE MODE (existant) ═══════ */
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                  Générer un nouveau relevé
                </h2>

            <div className="space-y-4">
              {/* Niveau - Premier champ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Niveau <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedNiveau}
                  onChange={(e) => setSelectedNiveau(e.target.value as NiveauType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="L1">L1</option>
                  <option value="L2">L2</option>
                  <option value="L3">L3</option>
                  <option value="M1">M1</option>
                  <option value="M2">M2</option>
                </select>
              </div>

              {/* Option - Deuxième champ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Option <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {Array.isArray(options) &&
                    options.map((option) => (
                      <option key={option.id} value={option.nom}>
                        {option.nom}
                      </option>
                    ))}
                </select>
              </div>

              {/* Semestre - Troisième champ (dynamique selon le niveau) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Semestre <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedSemestre}
                  onChange={(e) => setSelectedSemestre(e.target.value)}
                  disabled={!selectedNiveau}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!selectedNiveau ? '-- Sélectionnez d\'abord un niveau --' : '-- Sélectionner --'}
                  </option>
                  {semestresDisponibles.map((sem) => (
                    <option key={sem.value} value={sem.value}>
                      {sem.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Matière - Quatrième champ, filtré selon niveau/option/semestre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Matière <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedMatiere}
                  onChange={(e) => setSelectedMatiere(e.target.value)}
                  disabled={!selectedNiveau || !selectedOption || !selectedSemestre}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!selectedNiveau || !selectedOption || !selectedSemestre
                      ? '-- Sélectionnez d\'abord niveau, option et semestre --'
                      : filteredMatieres.length === 0
                      ? '-- Aucune matière trouvée --'
                      : '-- Sélectionner --'}
                  </option>
                  {filteredMatieres.map((matiere) => (
                    <option key={matiere.id} value={matiere.id}>
                      {matiere.code_matiere} - {matiere.nom_matiere}
                    </option>
                  ))}
                </select>
                {selectedNiveau && selectedOption && selectedSemestre && filteredMatieres.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Aucune matière pour cette combinaison niveau/option/semestre
                  </p>
                )}
              </div>

              {/* Enseignant - Auto-rempli ou éditable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enseignant / Formateur
                  {!enseignantEditable && enseignantNom && (
                    <span className="text-green-600 text-xs ml-2">(attribué)</span>
                  )}
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={enseignantNom}
                    onChange={(e) => setEnseignantNom(e.target.value)}
                    disabled={!enseignantEditable && !!enseignantNom}
                    placeholder={selectedMatiere ? 'Nom' : 'Sélectionnez une matière'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${
                      !enseignantEditable && enseignantNom ? 'bg-gray-50 dark:bg-gray-600' : ''
                    }`}
                  />
                  <input
                    type="text"
                    value={enseignantPrenoms}
                    onChange={(e) => setEnseignantPrenoms(e.target.value)}
                    disabled={!enseignantEditable && !!enseignantNom}
                    placeholder="Prénoms"
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${
                      !enseignantEditable && enseignantNom ? 'bg-gray-50 dark:bg-gray-600' : ''
                    }`}
                  />
                  {enseignantNom && !enseignantEditable && (
                    <button
                      type="button"
                      onClick={() => setEnseignantEditable(true)}
                      className="w-full px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                      title="Modifier l'enseignant"
                    >
                      Modifier
                    </button>
                  )}
                </div>
                {!enseignantNom && selectedMatiere && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Aucun formateur attribué. Saisissez un nom — il sera automatiquement enregistré.
                  </p>
                )}
              </div>

              {/* Date d'évaluation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date d'évaluation <span className="text-xs text-gray-400">(optionnel)</span>
                </label>
                <input
                  type="date"
                  value={dateEvaluation}
                  onChange={(e) => setDateEvaluation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              {/* Format du relevé */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Format du relevé
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    {
                      value: 'cases_separees' as const,
                      label: 'Cases Séparées',
                      badge: 'RECOMMANDÉ',
                      badgeColor: 'bg-green-100 text-green-800',
                      desc: 'Un chiffre par case (ex: [1][5][,][5]). Meilleure précision OCR.',
                    },
                    {
                      value: 'zone_guidee' as const,
                      label: 'Zone Guidée',
                      badge: 'STANDARD',
                      badgeColor: 'bg-blue-100 text-blue-800',
                      desc: 'Zone avec ligne pour écrire D1 et D2 + case de vérification.',
                    },
                    {
                      value: 'simple' as const,
                      label: 'Simple',
                      badge: 'BASIQUE',
                      badgeColor: 'bg-gray-100 text-gray-700',
                      desc: 'Zone libre pour écriture manuscrite.',
                    },
                  ].map((fmt) => (
                    <div
                      key={fmt.value}
                      onClick={() => setSelectedFormat(fmt.value)}
                      className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${
                        selectedFormat === fmt.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{fmt.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${fmt.badgeColor}`}>{fmt.badge}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{fmt.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Année académique - Affichage informatif seulement */}
              {anneeActive && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <span className="font-medium">Année académique :</span> {anneeActive.libelle}
                  </p>
                </div>
              )}

              {/* Bouton sticky en bas pour éviter le scroll interminable
                  quand le formulaire est long (champs empilés). */}
              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_10px_-6px_rgba(0,0,0,0.1)]">
                <button
                  onClick={handleGenererReleve}
                  disabled={genererLoading || !selectedMatiere || !selectedNiveau || !selectedOption || !selectedSemestre}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {genererLoading ? (
                    <Loader className="animate-spin mr-2" size={20} />
                  ) : (
                    <FileText className="mr-2" size={20} />
                  )}
                  Générer le relevé
                </button>
              </div>
            </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4">Instructions</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800 dark:text-blue-300">
              <li>Générez un relevé PDF avec le formulaire ci-dessus</li>
              <li>Téléchargez et imprimez le relevé</li>
              <li>L'enseignant remplit les notes manuellement</li>
              <li>Scannez le relevé rempli (300 DPI recommandé)</li>
              <li>Uploadez le scan et lancez le traitement OCR</li>
              <li>Vérifiez et validez les notes détectées</li>
            </ol>
          </div>
        </div>

        {/* Liste des relevés (refonte 2026-09-01 : col-span-2 → col-span-3
            pour occuper 75% de la largeur au lieu de 66%) */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            {/* En-tête + barre de filtres */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ListFilter size={20} className="text-blue-600" />
                  Relevés ({groupedReleves.reduce((s, g) => s + g.releves.length, 0)})
                </h2>
                <div className="flex gap-1.5 flex-wrap">
                  <button
                    onClick={() => setShowUploadBatchQR(true)}
                    title="Uploader plusieurs scans à la fois — routage automatique via QR code"
                    className="px-2 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Upload size={12} /> Upload en lot (QR)
                  </button>
                  <button
                    onClick={() => setShowHistoriqueImports(true)}
                    title="Voir l'historique des imports de scans"
                    className="px-2 py-1.5 rounded text-xs font-medium text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 flex items-center gap-1"
                  >
                    <History size={12} /> Historique imports
                  </button>
                  <button
                    onClick={() => {
                      const allKeys = groupedReleves.map(g => g.key);
                      setCollapsedGroups(new Set(allKeys));
                    }}
                    title="Tout replier"
                    className="p-1.5 rounded text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center gap-1"
                  >
                    <Minimize2 size={12} /> Tout replier
                  </button>
                  <button
                    onClick={() => setCollapsedGroups(new Set())}
                    title="Tout déplier"
                    className="p-1.5 rounded text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center gap-1"
                  >
                    <Maximize2 size={12} /> Tout déplier
                  </button>
                </div>
              </div>

              {/* Onglets statut */}
              <div className="flex gap-1 -mb-px overflow-x-auto">
                {([
                  { key: 'tous', label: 'Tous', count: compteursStatut.tous, color: 'gray' },
                  { key: 'a_traiter', label: 'À traiter', count: compteursStatut.a_traiter, color: 'amber' },
                  { key: 'traites', label: 'Traités', count: compteursStatut.traites, color: 'indigo' },
                  { key: 'valides', label: 'Validés', count: compteursStatut.valides, color: 'green' },
                ] as const).map(tab => {
                  const active = filterStatut === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setFilterStatut(tab.key)}
                      className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                        active
                          ? `border-${tab.color}-600 text-${tab.color}-700 dark:text-${tab.color}-400`
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      {tab.label}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        active
                          ? `bg-${tab.color}-100 text-${tab.color}-800 dark:bg-${tab.color}-900/40 dark:text-${tab.color}-300`
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Recherche + filtre niveau */}
              <div className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher (code, matière, classe, enseignant)…"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                      title="Effacer"
                    >
                      <XCircle size={14} />
                    </button>
                  )}
                </div>
                <select
                  value={filterPole}
                  onChange={(e) => setFilterPole(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  title="Filtrer par pôle"
                >
                  <option value="">Tous pôles</option>
                  {Array.isArray(poles) && poles.map((p) => (
                    <option key={p.id} value={p.nom}>{p.nom}</option>
                  ))}
                </select>
                <select
                  value={filterNiveau}
                  onChange={(e) => setFilterNiveau(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous niveaux</option>
                  <option value="L1">L1</option>
                  <option value="L2">L2</option>
                  <option value="L3">L3</option>
                  <option value="M1">M1</option>
                  <option value="M2">M2</option>
                </select>
                <select
                  value={filterSemestreParity}
                  onChange={(e) => setFilterSemestreParity(e.target.value as any)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  title="Filtrer par semestre (impair / pair)"
                >
                  <option value="">Tous semestres</option>
                  <option value="impair">Impair (S1 / S3 / S5)</option>
                  <option value="pair">Pair (S2 / S4 / S6)</option>
                </select>
              </div>
            </div>

            {relevesLoading ? (
              <div className="p-8 text-center">
                <Loader className="animate-spin mx-auto text-blue-600" size={32} />
                <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement...</p>
              </div>
            ) : groupedReleves.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Aucun relevé trouvé. Générez votre premier relevé.
              </div>
            ) : (
              <div>
                {/* Barre d'actions en masse */}
                {selectedIds.size > 0 && (
                  <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-700 flex-wrap gap-2">
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                      {selectedIds.size} relevé(s) sélectionné(s)
                      {bulkPrintLoading.active && (
                        <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-300">
                          ({bulkPrintLoading.current}/{bulkPrintLoading.total} traités…)
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={handleBulkPrint}
                        disabled={bulkPrintLoading.active}
                        title="Ouvre une fenêtre avec tous les PDFs empilés, prête pour Ctrl+P"
                        className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {bulkPrintLoading.active ? <Loader className="animate-spin mr-1" size={14} /> : <Printer size={14} className="mr-1" />}
                        Imprimer tout ({selectedIds.size})
                      </button>
                      <button
                        onClick={handleBulkDownload}
                        disabled={bulkPrintLoading.active}
                        title="Télécharge tous les PDFs séparément dans votre dossier de téléchargements"
                        className="flex items-center px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {bulkPrintLoading.active ? <Loader className="animate-spin mr-1" size={14} /> : <Download size={14} className="mr-1" />}
                        Télécharger PDFs
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteLoading || bulkPrintLoading.active}
                        className="flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {bulkDeleteLoading ? <Loader className="animate-spin mr-1" size={14} /> : <Trash2 size={14} className="mr-1" />}
                        Supprimer ({selectedIds.size})
                      </button>
                      <button
                        onClick={() => setSelectedIds(new Set())}
                        className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Désélectionner
                      </button>
                    </div>
                  </div>
                )}
                {niveauxGroupes.map((nivGrp) => {
                  const niveauCollapsed = collapsedNiveaux.has(nivGrp.niveau);
                  const ns = niveauStyle(nivGrp.niveau);
                  return (
                    <div key={`niv-${nivGrp.niveau}`} className={`border-l-4 ${ns.border}`}>
                      {/* ═══ BANNIÈRE NIVEAU ═══ */}
                      <div
                        onClick={() => toggleNiveau(nivGrp.niveau)}
                        className={`sticky top-0 z-[5] flex items-center justify-between px-5 py-3 ${ns.bg} text-white shadow-md cursor-pointer hover:brightness-110 transition`}
                      >
                        <div className="flex items-center gap-3">
                          {niveauCollapsed
                            ? <ChevronRight size={20} className="text-white/90" />
                            : <ChevronDown size={20} className="text-white/90" />}
                          <span className="text-2xl font-extrabold tracking-wider">{nivGrp.niveau}</span>
                          <span className="text-xs font-medium text-white/90">
                            {nivGrp.classes.length} classe{nivGrp.classes.length > 1 ? 's' : ''}
                            {' · '}
                            {nivGrp.nbReleves} relevé{nivGrp.nbReleves > 1 ? 's' : ''}
                          </span>
                        </div>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm font-semibold">
                          {nivGrp.nbReleves}
                        </span>
                      </div>

                      {/* ═══ CLASSES DU NIVEAU ═══ */}
                      {!niveauCollapsed && nivGrp.classes.map((group) => {
                  const isCollapsed = collapsedGroups.has(group.key);
                  return (
                    <div key={group.key}>
                      {/* En-tête de groupe (classe) */}
                      <div
                        className={`flex items-center justify-between px-5 py-2.5 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={group.releves.every((r: ReleveDevoirs) => selectedIds.has(r.id))}
                            onChange={() => toggleSelectClasse(group.releves)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div onClick={() => toggleGroup(group.key)} className="flex items-center gap-3 flex-1">
                            {isCollapsed ? <ChevronRight size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${ns.badge}`}>
                              {group.niveau}
                            </span>
                            <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                              {group.label}
                            </span>
                          </div>
                        </div>
                        <span onClick={() => toggleGroup(group.key)} className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                          {group.releves.length} relevé(s)
                        </span>
                      </div>
                      {/* Relevés du groupe */}
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {group.releves.map((releve) => (
                  <div key={releve.id} className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 relative ${ocrEnCours.has(releve.id) ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}>
                    {ocrEnCours.has(releve.id) && (
                      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded">
                        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-pulse" />
                        <div className="absolute inset-x-0 bottom-0 h-0.5">
                          <div className="h-full bg-indigo-500 animate-[shimmer_2s_ease-in-out_infinite]" style={{ width: '40%', animation: 'shimmer 2s ease-in-out infinite' }} />
                        </div>
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(releve.id)}
                          onChange={() => toggleSelect(releve.id)}
                          className="w-4 h-4 mt-1.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {releve.code_releve}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              statusLabels[releve.status]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            {statusLabels[releve.status]?.icon}
                            <span className="ml-1">{statusLabels[releve.status]?.label || releve.status}</span>
                          </span>
                          {ocrEnCours.has(releve.id) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 animate-pulse">
                              <Loader className="animate-spin mr-1" size={12} />
                              OCR en cours... {ocrTimers[releve.id] ? formatTimer(ocrTimers[releve.id]) : ''}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {releve.matiere_code} - <span className="font-semibold text-blue-600 dark:text-blue-400">{releve.matiere_nom}</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {releve.niveau} | {releve.option} | {releve.semestre} | {releve.annee_academique}
                        </div>
                        {releve.enseignant_nom && (
                          <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                            <User size={12} className="text-amber-600 dark:text-amber-400" />
                            <span className="font-medium">Formateur :</span>
                            <span className="text-amber-700 dark:text-amber-300">{releve.enseignant_nom}</span>
                          </div>
                        )}
                        <div className="mt-2 flex items-center flex-wrap gap-2 text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {releve.nombre_etudiants} étudiants
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {releve.nombre_pages} pages
                          </span>
                          {/* Notes détectées (mirror examens) */}
                          {(releve.total_notes_detectees || 0) > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                              <CheckCircle2 size={12} />
                              {releve.total_notes_detectees} notes
                              {(releve.notes_a_verifier || 0) > 0 && (
                                <span className="text-orange-600 dark:text-orange-400">({releve.notes_a_verifier} à vérifier)</span>
                              )}
                            </span>
                          )}
                          {/* Date upload + uploadeur */}
                          {releve.date_scan && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800" title="Date d'upload">
                              <Clock size={12} /> Uploadé : {formatDate(releve.date_scan)}
                            </span>
                          )}
                          {releve.scanne_par_nom && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800" title="Utilisateur qui a uploadé le scan">
                              <User size={12} /> Par : <strong>{releve.scanne_par_nom}</strong>
                            </span>
                          )}
                          {/* Validateur */}
                          {releve.valide_par_nom && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800" title="Utilisateur qui a validé le relevé">
                              <CheckCircle2 size={12} /> Validé par : <strong>{releve.valide_par_nom}</strong>
                            </span>
                          )}
                          <span className="text-gray-400 dark:text-gray-500">
                            {formatDate(releve.date_generation)}
                          </span>
                          {releve.lignes_a_verifier > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-medium">
                              <AlertTriangle size={11} />
                              {releve.lignes_a_verifier} à vérifier
                            </span>
                          )}
                          {releve.total_notes_detectees > 0 && releve.lignes_a_verifier === 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 font-medium">
                              <CheckCircle2 size={11} />
                              {releve.total_notes_detectees} notes OK
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {/* Boutons d'actions principaux */}
                        <div className="flex flex-col space-y-1">
                          {/* Télécharger PDF */}
                          <button
                            onClick={() => handleDownloadPDF(releve)}
                            className="flex items-center px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                            title="Télécharger le PDF"
                          >
                            <Download size={16} className="mr-1" />
                            Télécharger
                          </button>

                          {/* Aperçu */}
                          <button
                            onClick={() => handlePreviewPDF(releve)}
                            className="flex items-center px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                            title="Aperçu du PDF"
                          >
                            <ExternalLink size={16} className="mr-1" />
                            Aperçu
                          </button>
                        </div>

                        {/* Actions selon le statut */}
                        <div className="flex flex-col space-y-1">
                          {/* Upload Scan (initial ou re-upload) */}
                          {releve.status !== 'valide' && (
                            <button
                              onClick={() => setSelectedReleveForUpload(releve)}
                              className={`flex items-center px-3 py-1.5 text-sm rounded ${
                                ['scanne', 'traite', 'verifie'].includes(releve.status)
                                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                              }`}
                              title={['scanne', 'traite', 'verifie'].includes(releve.status) ? 'Re-uploader le scan (remplacer)' : 'Uploader le scan'}
                            >
                              <Upload size={16} className="mr-1" />
                              {['scanne', 'traite', 'verifie'].includes(releve.status) ? 'Re-upload' : 'Upload'}
                            </button>
                          )}

                          {/* Lancer OCR */}
                          {releve.status === 'scanne' && (
                            <button
                              onClick={() => handleLancerOCR(releve)}
                              disabled={ocrLoading}
                              className="flex items-center px-3 py-1.5 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 disabled:opacity-50"
                              title="Lancer le traitement OCR"
                            >
                              {ocrLoading ? (
                                <Loader size={16} className="mr-1 animate-spin" />
                              ) : (
                                <Play size={16} className="mr-1" />
                              )}
                              OCR
                            </button>
                          )}

                          {/* Relancer OCR (après traitement) */}
                          {['traite', 'verifie'].includes(releve.status) && (
                            <button
                              onClick={() => handleLancerOCR(releve)}
                              disabled={ocrLoading}
                              className="flex items-center px-3 py-1.5 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 disabled:opacity-50"
                              title="Relancer le traitement OCR"
                            >
                              {ocrLoading ? (
                                <Loader size={16} className="mr-1 animate-spin" />
                              ) : (
                                <Play size={16} className="mr-1" />
                              )}
                              Relancer OCR
                            </button>
                          )}

                          {/* Vérifier */}
                          {['traite', 'verifie'].includes(releve.status) && (
                            <button
                              onClick={() => handleVerifier(releve)}
                              className="flex items-center px-3 py-1.5 text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/50"
                              title="Vérifier les notes"
                            >
                              <Eye size={16} className="mr-1" />
                              Vérifier
                            </button>
                          )}

                          {/* Validé : badge + bouton Consulter (image + notes en lecture seule) */}
                          {releve.status === 'valide' && (
                            <>
                              <span className="flex items-center px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                <CheckCircle2 size={16} className="mr-1" />
                                Validé
                              </span>
                              <button
                                onClick={() => handleVerifier(releve)}
                                className="flex items-center px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                                title="Consulter le relevé (image + notes)"
                              >
                                <Eye size={16} className="mr-1" />
                                Consulter
                              </button>
                            </>
                          )}

                          {/* Retour en cours : disponible pour traite/verifie/valide */}
                          {['traite', 'verifie', 'valide'].includes(releve.status) && (
                            <button
                              onClick={() => setSelectedReleveForRetour(releve)}
                              className="flex items-center px-3 py-1.5 text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50"
                              title="Retourner ce relevé pour relancer l'OCR"
                            >
                              <RotateCcw size={16} className="mr-1" />
                              Retour
                            </button>
                          )}
                        </div>

                        {/* Menu contextuel */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === releve.id ? null : releve.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenuId === releve.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                              <button
                                onClick={() => {
                                  setSelectedReleveForDetails(releve);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <FileSearch size={16} className="mr-2" />
                                Détails
                              </button>
                              <button
                                onClick={() => {
                                  navigate(`/ocr/historique-devoirs/${releve.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <History size={16} className="mr-2" />
                                Historique
                              </button>
                              <hr className="my-1" />
                              <button
                                onClick={() => {
                                  setSelectedReleveForDelete(releve);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 size={16} className="mr-2" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Upload Scan */}
      {selectedReleveForUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {['scanne', 'traite', 'verifie'].includes(selectedReleveForUpload.status)
                ? `Re-uploader le scan - ${selectedReleveForUpload.code_releve}`
                : `Uploader le scan - ${selectedReleveForUpload.code_releve}`
              }
            </h3>
            {['scanne', 'traite', 'verifie'].includes(selectedReleveForUpload.status) && (
              <div className="mb-3 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded text-sm text-orange-700 dark:text-orange-300">
                Ce relevé a déjà été scanné. Le nouveau fichier remplacera l'ancien et les résultats OCR précédents seront supprimés.
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fichier scanné (PDF ou images)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Format recommandé: PDF multi-pages ou images à 300 DPI
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleUploadScan}
                disabled={uploadLoading || !uploadFile}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Upload className="mr-2" size={18} />
                )}
                Uploader
              </button>
              <button
                onClick={() => {
                  setSelectedReleveForUpload(null);
                  setUploadFile(null);
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {selectedReleveForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Détails du relevé - {selectedReleveForDetails.code_releve}
              </h3>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Matière</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.matiere_code} - <span className="text-blue-600 dark:text-blue-400">{selectedReleveForDetails.matiere_nom}</span></p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Niveau / Option</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.niveau} - {selectedReleveForDetails.option}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Semestre</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.semestre}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Année académique</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.annee_academique}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
                  <p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[selectedReleveForDetails.status]?.color}`}>
                      {statusLabels[selectedReleveForDetails.status]?.label}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre d'étudiants</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_etudiants}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre de pages</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_pages}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Date de génération</span>
                  <p className="font-medium dark:text-gray-200">{formatDate(selectedReleveForDetails.date_generation)}</p>
                </div>
              </div>
            </div>

            {/* Statistiques OCR si traité */}
            {['traite', 'verifie', 'valide'].includes(selectedReleveForDetails.status) && (
              <div className="mt-6 pt-4 border-t dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Résultats OCR</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Notes détectées</span>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{selectedReleveForDetails.total_notes_detectees}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Confiance haute</span>
                    <p className="text-xl font-bold text-green-600">{selectedReleveForDetails.notes_confiance_haute}</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">À vérifier</span>
                    <p className="text-xl font-bold text-orange-600">{selectedReleveForDetails.notes_a_verifier}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => handleDownloadPDF(selectedReleveForDetails)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Download size={18} className="mr-2" />
                Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Retour en cours */}
      {selectedReleveForRetour && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <RotateCcw className="text-orange-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Retourner le relevé en cours
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Permet de relancer l'OCR
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Relevé <strong>{selectedReleveForRetour.code_releve}</strong> ({statusLabels[selectedReleveForRetour.status]?.label})
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {selectedReleveForRetour.matiere_code} — {selectedReleveForRetour.matiere_nom}
            </p>

            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-3 mb-4 text-sm text-orange-700 dark:text-orange-300">
              <strong>Conséquences :</strong>
              <ul className="mt-1 ml-4 list-disc text-xs">
                <li>Toutes les lignes OCR seront supprimées</li>
                <li>Les compteurs de notes seront remis à zéro</li>
                <li>Le statut repassera à <strong>« Scanné »</strong> (ou « Généré » si scan supprimé)</li>
                {selectedReleveForRetour.status === 'valide' && (
                  <li>La validation existante sera annulée</li>
                )}
              </ul>
            </div>

            <label className="flex items-center mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={retourSupprimerScan}
                onChange={(e) => setRetourSupprimerScan(e.target.checked)}
                className="mr-2 rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Supprimer aussi le fichier scanné existant
              </span>
            </label>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motif (optionnel, pour traçabilité)
              </label>
              <input
                type="text"
                value={retourMotif}
                onChange={(e) => setRetourMotif(e.target.value)}
                placeholder="ex: OCR incorrect, scan flou…"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setSelectedReleveForRetour(null); setRetourSupprimerScan(false); setRetourMotif(''); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
              <button
                onClick={handleRetournerEnCours}
                disabled={retourLoading}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {retourLoading ? <Loader size={16} className="mr-2 animate-spin" /> : <RotateCcw size={16} className="mr-2" />}
                Retourner en cours
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {selectedReleveForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Confirmer la suppression
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cette action est irréversible
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Êtes-vous sûr de vouloir supprimer le relevé <strong>{selectedReleveForDelete.code_releve}</strong> ?
              Tous les fichiers et données associés seront définitivement supprimés.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteReleve}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Trash2 className="mr-2" size={18} />
                )}
                Supprimer
              </button>
              <button
                onClick={() => setSelectedReleveForDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fermer menu contextuel en cliquant ailleurs */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenMenuId(null)}
        />
      )}

      {/* Modal d'upload en lot par QR */}
      <UploadBatchParQRModal
        open={showUploadBatchQR}
        onClose={() => setShowUploadBatchQR(false)}
        type="devoirs"
      />

      {/* Modal historique imports */}
      <HistoriqueImportsModal
        open={showHistoriqueImports}
        onClose={() => setShowHistoriqueImports(false)}
        typeFilter="devoirs"
      />

      {/* Bouton flottant « Retour en haut » — apparaît dès qu'on scrolle */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

export default GenerationReleveDevoirs;
